/**
 * Logique pure de l'écran de lettrage : la sélection d'écritures et ce
 * qu'elle annonce, sans React ni appel réseau.
 *
 * Les montants s'additionnent en centimes entiers : additionner des nombres à
 * virgule flottante finit par afficher un solde de 0,00000001 là où le
 * comptable attend zéro.
 */

export interface EcritureAApparier {
  piece: string;
  rang: number;
  debit: number;
  credit: number;
}

/** Clé d'une écriture dans la sélection : la pièce et le rang de la ligne. */
export const cleDe = (ecriture: { piece: string; rang: number }): string => `${ecriture.piece}:${ecriture.rang}`;

export function basculer(selection: ReadonlySet<string>, cle: string): Set<string> {
  const suivante = new Set(selection);
  if (suivante.has(cle)) suivante.delete(cle);
  else suivante.add(cle);
  return suivante;
}

export function selectionDe(ecritures: readonly { piece: string; rang: number }[]): Set<string> {
  return new Set(ecritures.map(cleDe));
}

export const enCentimes = (montant: number): number => Math.round(montant * 100);

export interface TotauxDeSelection {
  nombre: number;
  debit: number;
  credit: number;
  /** Débit moins crédit, en dirhams : nul quand la sélection se solde. */
  solde: number;
  estEquilibree: boolean;
}

export function totauxDeSelection(
  ecritures: readonly EcritureAApparier[],
  selection: ReadonlySet<string>,
): TotauxDeSelection {
  let nombre = 0;
  let debit = 0;
  let credit = 0;
  for (const ecriture of ecritures) {
    if (!selection.has(cleDe(ecriture))) continue;
    nombre += 1;
    debit += enCentimes(ecriture.debit);
    credit += enCentimes(ecriture.credit);
  }
  const solde = debit - credit;
  return { nombre, debit: debit / 100, credit: credit / 100, solde: solde / 100, estEquilibree: solde === 0 };
}

export type StatutPrevu = "VIDE" | "TOTAL" | "PARTIEL";

/** Ce que donnerait le lettrage de la sélection telle qu'elle est. */
export function statutPrevu(totaux: TotauxDeSelection): StatutPrevu {
  if (totaux.nombre === 0) return "VIDE";
  return totaux.estEquilibree ? "TOTAL" : "PARTIEL";
}

export type NatureDeLEcart = "CHARGE" | "PRODUIT";

/**
 * Nature de l'écart de règlement qui solderait un lettrage partiel.
 *
 * Un solde créditeur sur le tiers — le fournisseur a été réglé un peu moins
 * que sa facture, le client a payé un peu plus — se solde par un débit du
 * tiers et un produit ; un solde débiteur, par un crédit du tiers et une
 * charge. Le compte de charge ou de produit est un compte spécial du
 * référentiel, jamais choisi ici.
 */
export function natureDeLEcart(solde: number): NatureDeLEcart | null {
  const centimes = enCentimes(solde);
  if (centimes === 0) return null;
  return centimes > 0 ? "CHARGE" : "PRODUIT";
}

export const LIBELLE_DE_LA_NATURE: Record<NatureDeLEcart, string> = {
  CHARGE: "charge",
  PRODUIT: "produit",
};
